<template>
  <main class="layout__main pt-0">
    <!-- Intro: BEGIN -->
    <Intro
      class="event-info"
      :image="image"
      :mobileImage="hasMobileImage ? mobileImage : image"
      :loaded="!!title"
    >
      <template v-slot:title>
        <h1 class="event-info__title">{{ title }}</h1>
        <div class="event-info__badges">
          <div class="event-badges">
            <div
              class="event-badges__item"
              v-if="badges.rrr"
            >
              <v-popover placement="top-center" trigger="hover">
                <img
                  src="@/assets/img/badges/recommended-rr.svg"
                  alt="RR"
                >
                <template slot="popover">
                  <div v-html="$t('race.rrTooltip')"></div>
                </template>
              </v-popover>
            </div>
            <div
              class="event-badges__item"
              v-if="badges.sportmaster"
            >
              <v-popover placement="top-center" trigger="hover">
                <img
                  src="@/assets/img/badges/sportmaster-icon.svg"
                  alt="SM"
                >
                <template slot="popover">
                  <div v-html="$t('race.sportmasterTooltip')"></div>
                </template>
              </v-popover>
            </div>
            <div
              class="event-badges__item"
              v-if="badges.nrm"
            >
              <v-popover placement="top-center" trigger="hover">
                <img
                  src="@/assets/img/badges/nbd.svg"
                  alt="NBD"
                >
                <template slot="popover">
                  <div v-html="$t('race.nroTooltip')"></div>
                </template>
              </v-popover>
            </div>
            <div
              class="event-badges__item"
              v-if="badges.sr || badges.asr"
            >
              <v-popover placement="top-center" trigger="hover">
                <div
                  class="stars"
                  :class="{'stars_approved': badges.asr}"
                >
                  <icon icon-name="star"></icon>
                  <span class="stars__text">{{ badges.asr || badges.sr }}</span>
                </div>
                <template slot="popover">
                  <div
                    v-html="$t(badges.asr ? 'race.starRatingApproved' : 'race.starRating')"
                  ></div>
                </template>
              </v-popover>
            </div>
            <div
              class="event-badges__item"
              v-if="badges.pra"
            >
              <v-popover
                placement="top-center"
                trigger="hover"
                popoverClass="member-rating-popup"
              >
                <badge
                  class="member-rating-badge badge_circle"
                  :text="badges.pra"
                >
                </badge>
                <template slot="popover">
                  <table class="">
                    <tr v-for="(item, i) in badges.praPopup" :key="i">
                      <td>{{ i }}</td>
                      <td><b>{{ item }}</b></td>
                    </tr>
                  </table>
                </template>
              </v-popover>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:details>
        <div class="event-info__details">
          <div class="event-info__detail">
            {{ date }}
          </div>
          <div class="event-info__detail">
            {{ place }}
          </div>
        </div>
      </template>
      <template v-slot:actions v-if="(finished ? racesWithResults.length : racesWithReg.length) || (showIsMember && isMember)">
        <div class="rr-buttons-group rr-buttons-group_flex">
          <Button
            v-if="showIsMember && isMember"
            variant="ghost"
            view="success"
            disabled-quiet
            :text="$t('event.isMember')"
            icon-right="check-bold"
          ></Button>
          <!-- Live button -->
          <Button
            v-if="eventState === 'live' && racesWithLive.length"
            variant="primary"
            view="danger"
            wide
            v-open-modal="{ name: 'live-modal' }"
            :text="$t('race.liveStream')"
            icon-right="play"
          ></Button>
          <!-- Result button -->
          <Button
            v-else-if="eventState === 'result' && racesWithResults.length"
            variant="primary"
            v-open-modal="{ name: 'results-modal' }"
            :text="$t('race.results')"
            icon-right="right"
          ></Button>
          <!-- Registation button -->
          <template v-else-if="eventState === 'registration' && racesWithReg.length">
            <Button
              v-if="isMember"
              variant="primary"
              v-open-modal="{ name: 'reg-modal' }"
              text="+1"
            ></Button>
            <Button
              v-else
              variant="primary"
              v-open-modal="{ name: 'reg-modal' }"
              :text="$t('event.registration')"
              icon-right="right"
            ></Button>
          </template>
        </div>
      </template>
    </Intro>
    <!-- Intro: END -->
    <div id="eventNav" style="position: relative; top: 64px;"></div>
    <div class="event-nav" id="stickyNav">
      <div class="event-nav__container">
        <div class="grid grid_v-center">
          <div class="grid__col grid__col_stretch">
            <tabs-nav :items="nav"></tabs-nav>
          </div>
          <div class="grid__col grid__col_auto">
            <Button
              variant="secondary"
              shape="circle"
              v-open-modal="{ name: 'invite-modal' }"
              icon-left="upload"
            ></Button>
          </div>
          <div
            class="grid__col grid__col_auto d-none"
            v-if="showIsMember && isMember ||
            finished && racesWithResults.length ||
            !finished && racesWithReg.length"
            v-show="navIsSticky"
            :class="{'d-lg-block': navIsSticky}"
          >
            <div class="rr-buttons-group rr-buttons-group_flex">
              <Button
                v-if="showIsMember && isMember"
                variant="ghost"
                view="success"
                disabled-quiet
                :text="$t('event.isMember')"
                icon-right="check-bold"
              ></Button>
              <!-- Live button -->
              <Button
                v-if="eventState === 'live' && racesWithLive.length"
                variant="primary"
                view="danger"
                wide
                v-open-modal="{ name: 'live-modal' }"
                :text="$t('race.liveStream')"
                icon-right="play"
              ></Button>
              <!-- Result button -->
              <Button
                v-else-if="eventState === 'result' && racesWithResults.length"
                variant="primary"
                v-open-modal="{ name: 'results-modal' }"
                :text="$t('race.results')"
                icon-right="right"
              ></Button>
              <!-- Registration button -->
              <template v-else-if="eventState === 'registration' && racesWithReg.length">
                <Button
                  v-if="isMember"
                  variant="primary"
                  v-open-modal="{ name: 'reg-modal' }"
                  text="+1"
                ></Button>
                <Button
                  v-else
                  variant="primary"
                  v-open-modal="{ name: 'reg-modal' }"
                  :text="$t('event.registration')"
                  icon-right="right"
                ></Button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="event-alerts container">
      <div class="grid grid_h-center">
        <div class="grid__col grid__col_xl-20">
          <draft-alert/>
        </div>
      </div>
    </div>
    <section class="section m-0">
      <div class="section__group">
        <keep-alive>
          <router-view
            :event="event"
            :races="races"
            :dynamicRacesById="dynamicRacesById"
            :finished="finished"
            :isLive="isLive"
            :showStartList="showStartList"
            :hasMembers="hasMembers"
          ></router-view>
        </keep-alive>
      </div>
    </section>
    <invite-modal v-if="title" :shareTitle="title + ' - RussiaRunning'"></invite-modal>
    <offline-reg-modal></offline-reg-modal>
    <about-online-modal></about-online-modal>
    <modal
      class="reg-modal"
      name="reg-modal"
      align="center"
      ref="modal"
      v-if="racesWithReg.length"
    >
      <div class="base-modal__heading base-modal__item">
        <h3 class="base-modal__title">{{ $t('registrationModal.title') }}</h3>
      </div>
      <div class="base-modal__body base-modal__item">
        <event-races
          :utcCurrentTime="utcCurrentTime"
          :scountInfo="scountInfo"
          :races="racesWithReg"
          :dynamicRacesById="dynamicRacesById"
          :finished="finished"
          :event="event"
          :group-by-day="!finished"
          :raceProps="{
            registerText: $t('race.choose'),
            compact: true,
          }"
          @click:aboutOnline="() => {
            $root.$modals['reg-modal'].close();
            $root.$modals['about-online-modal'].open();
          }"
        ></event-races>
      </div>
      <div class="base-modal__item" v-if="!finished && event.hasCommission && event.commission">
        <div class="text-xs color-muted text-center" v-html="$t('event.slotCommission', [`${event.commission.toLocaleString($lang)}%`])"></div>
      </div>
    </modal>
    <modal
      class="reg-modal"
      name="results-modal"
      align="center"
      ref="modal"
      v-if="racesWithResults.length"
    >
      <div class="base-modal__heading base-modal__item">
        <h3 class="base-modal__title">{{ $t('resultsModal.title') }}</h3>
      </div>
      <div class="base-modal__body base-modal__item">
        <event-races
          :utcCurrentTime="utcCurrentTime"
          :scountInfo="scountInfo"
          :races="racesWithResults"
          :dynamicRacesById="dynamicRacesById"
          :finished="finished"
          :event="event"
          :raceProps="{
            resultsText: $t('race.choose'),
            compact: true,
          }"
        ></event-races>
      </div>
    </modal>
    <modal
      class="reg-modal"
      name="live-modal"
      align="center"
      ref="modal"
      v-if="racesWithLive.length"
    >
      <div class="base-modal__heading base-modal__item">
        <h3 class="base-modal__title">{{ $t('resultsModal.title') }}</h3>
      </div>
      <div class="base-modal__body base-modal__item">
        <event-races
          :utcCurrentTime="utcCurrentTime"
          :scountInfo="scountInfo"
          :races="racesWithLive"
          :dynamicRacesById="dynamicRacesById"
          :finished="finished"
          :event="event"
          :raceProps="{
            liveText: $t('race.choose'),
            compact: true,
            showIsLive: true,
          }"
        ></event-races>
      </div>
    </modal>
  </main>
</template>

<script>
import eventsService from '@/services/eventsService';
import Intro from '@/components/Intro.vue';
import TabsNav from '@/components/TabsNav.vue';
import Modal from '@/components/Modal.vue';
import InviteModal from '@/components/InviteModal.vue';
import OfflineRegModal from '@/components/OfflineRegModal.vue';
import AboutOnlineModal from '@/components/AboutOnlineModal.vue';
import EventRaces from '@/components/EventRaces.vue';
import DraftAlert from '@/components/DraftAlert.vue';
import Badge from '@/components/Badge.vue';
import { getCroppedImg } from '@/assets/js/helpers';
import { sportTypesMixin } from '@/assets/js/mixins';
import getScountInfo from '@/services/getScountInfo';

export default {
  name: 'Event',
  components: {
    Intro,
    TabsNav,
    Modal,
    InviteModal,
    OfflineRegModal,
    AboutOnlineModal,
    EventRaces,
    DraftAlert,
    Badge,
  },
  mixins: [sportTypesMixin],
  data() {
    return {
      utcCurrentTime: this.getCurrentUtcTime(),
      updateIntervalUtcId: null,
      event: {},
      eventParticipantRating: null,
      title: null,
      place: null,
      beginDate: null,
      endDate: null,
      badges: {},
      discipline: null,
      eventLoaded: false,
      image: null,
      mobileImage: null,
      hasMobileImage: false,
      races: null,
      racesWithLive: [],
      racesWithReg: [],
      racesWithResults: [],
      isMember: false,
      navIsSticky: false,
      dynamicRaces: [],
      dynamicRacesById: null,
    };
  },
  computed: {
    scountInfo() {
      if (!this.races || !this.event) return;

      return this.getScountInfo(this.races, this.event);
    },
    eventState() {
      if (this.isLive) {
        return 'live';
      }
      if (this.finished) {
        return 'result';
      }
      return 'registration';
    },
    nav() {
      const nav = [
        {
          name: this.$t('event.nav.overview'),
          link: {
            name: 'Event',
          },
        },
      ];
      if (this.showStartList) {
        nav.push({
          name: this.$t('event.nav.members'),
          link: {
            name: 'Members',
          },
        });
      }
      return nav;
    },
    isLive() {
      return (this.dynamicRaces || []).some((race) => race.IsLive);
    },
    resultsLink() {
      const url = window.location.origin;
      const eventCode = this.$route.params.code;
      return `${url}/event/${eventCode}/results`;
    },
    finished() {
      if (!this.event) {
        return false;
      }
      let end = this.$moment(this.event.EndDate).utcOffset(this.event.TimeZoneOffset * 60, true);
      end = this.$moment.utc(end);
      const now = this.$moment.utc();
      return end.isBefore(now);
    },
    showStartList() {
      if (!this.races) {
        return false;
      }
      return this.races.some((race) => race.sl && race.rs !== 1);
    },
    hasMembers() {
      if (!this.races) {
        return false;
      }
      return this.races.some((race) => race.pc);
    },
    date() {
      if (!this.event) {
        return null;
      }
      let begin = this.beginDate;
      let end = this.endDate;

      const locale = this.$lang === 'uz' ? 'uz-latn' : this.$lang;
      this.$moment.locale(locale);

      if (begin) {
        begin = this.$moment(begin);
      }

      if (end) {
        end = this.$moment(end);
      }

      if (!begin) {
        return null;
      }

      const format = 'DD MMMM YYYY';

      if (end && end.isSame(begin, 'day')) {
        return begin.format(format);
      }

      return `${begin.format(format)} – ${end.format(format)}`;
    },
    showIsMember() {
      return !this.finished && !this.isLive;
    },
  },
  methods: {
    getCurrentUtcTime() {
      return new Date().toISOString().slice(0, -1);
    },
    getScountInfo,
    setData() {
      const { event } = this;

      if (!event) {
        return;
      }

      this.title = event.Title;
      this.place = event.Place;
      this.beginDate = event.BeginDate;
      this.endDate = event.EndDate;

      this.badges = {
        rrr: event.rrr,
        nrm: event.InNrm,
        sr: !event.hw && event.sr,
        asr: !event.har && event.asr,
        pra: event.pra && Math.round(+event.pra.replace(',', '.') * 10) / 10,
        praPopup: this.eventParticipantRating || {},
      };

      this.eventLoaded = true;

      this.setImage('image', getCroppedImg(this.event.ImageUrl, [null, 748]));
      this.hasMobileImage = !!this.event.MobileImageUrl;
      if (this.hasMobileImage) {
        this.setImage('mobileImage', getCroppedImg(this.event.MobileImageUrl, [null, 960]));
      }

      this.setRaces();
    },
    setImage(key, imageUrl) {
      const url = imageUrl;
      const img = new Image();
      const d = this.discipline || this.event.dc;

      const { sportTypesImages } = this;

      let imgUrl = sportTypesImages.run.imgLg;
      if (sportTypesImages[d] && sportTypesImages[d].imgLg) {
        imgUrl = sportTypesImages[d].imgLg;
      }

      img.onload = () => {
        this[key] = url;
      };
      img.onerror = () => {
        this[key] = `${process.env.VUE_APP_URL}${imgUrl}`;
      };
      img.src = url;
    },
    setRaces() {
      const { event } = this;

      if (!event || !event.Races) {
        return;
      }

      const races = event.Races.filter((race) => {
        const dynamicRace = this.dynamicRacesById[race.id];
        return this.finished ? !race.hr : dynamicRace && !dynamicRace.DisableRegistration;
      });

      this.races = races;
      this.racesWithReg = [];
      this.racesWithResults = [];
      this.racesWithLive = [];
      this.isMember = false;

      for (let i = 0; i < races.length; i += 1) {
        const race = races[i];

        const dynamicRace = this.dynamicRacesById[race.id];

        const dynamicAvailable = dynamicRace && (!dynamicRace.DisableRegistration && dynamicRace.AvailableRegistrationsCount !== 0);

        const registrationAvailable = race.rs === 2 && dynamicAvailable;
        const availableFriendReg = race.ism ? !race.dfr : true;
        const isOther = race.dc === 'other';

        if (!isOther) {
          if (dynamicRace && dynamicRace.IsLive) {
            this.racesWithLive.push(race);
          }
          if (registrationAvailable && availableFriendReg) {
            this.racesWithReg.push(race);
          }
          if (race.rsc && !race.pc < 1) {
            this.racesWithResults.push(race);
          }
          if (race.ism) {
            this.isMember = true;
          }
        }
      }
    },
    setDynamicRaces(data) {
      const races = data.reduce((acc, race) => {
        acc[race.RaceId] = race;

        return acc;
      }, {});

      this.dynamicRacesById = races;

      this.dynamicRaces = data;
    },
    setPassedData(data) {
      if (!data) {
        return;
      }
      this.title = data.name;
      this.place = data.location;
      this.beginDate = data.beginDate;
      this.endDate = data.endDate;
      this.badges = data.badges;
      this.discipline = data.discipline;
    },
  },
  async created() {
    const eventData = this.$route.params.fullEvent;

    if (eventData) {
      this.event = eventData;
      this.setData();
      return;
    }

    this.setPassedData(this.$route.params.event);

    const eventResponse = await eventsService.getEventDetails(this.$route.params.code);
    const dynamcEventResponse = await eventsService.getDynamicEventDetails(this.$route.params.code);

    if (!eventResponse.data) {
      window.document.location.href = '/events';
      return;
    }

    const event = eventResponse.data;
    this.setDynamicRaces(dynamcEventResponse.data);

    const participantRatingResponse = await eventsService.getEventParticipantRating(event.Id);
    this.eventParticipantRating = participantRatingResponse.data;

    this.event = event;
    this.setData();
  },
  mounted() {
    this.updateIntervalUtcId = setInterval(() => {
      this.utcCurrentTime = this.getCurrentUtcTime();
    }, 10000);

    const stickyNav = document.getElementById('stickyNav');
    const observer = new IntersectionObserver(
      ([e]) => {
        const { top } = stickyNav.getBoundingClientRect();
        this.navIsSticky = e.intersectionRatio < 1 && top < 10;
      }, {
        rootMargin: '-1px 0px 0px 0px',
        threshold: [1],
      },
    );
    observer.observe(stickyNav);
  },
  unmounted() {
    clearInterval(this.updateIntervalUtcId);
  },
};
</script>

<style lang="scss">
  .event-info {

    &__title,
    &__badges {
      vertical-align: middle;
      margin-right: 16px;
    }

    &__title {
      display: inline;
    }

    &__badges {
      display: inline-block;
      padding: 4px 0;
    }

    &__details {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-top: -8px;
      margin-left: -40px;
      padding-right: 20px;
    }

    &__detail {
      flex: 0 0 auto;
      max-width: 100%;
      padding-left: 20px;
      margin-left: 20px;
      margin-top: 8px;
      font-weight: $font-weight-semibold;
      opacity: 0.85;
      position: relative;

      &::before {
        content: "";
        display: block;
        position: absolute;
        top: 50%;
        left: 0;
        width: 1px;
        height: 20px;
        margin-top: -10px;
        border-right: 1px solid;
        opacity: 0.45;
      }
    }
  }

  .event-badges {
    display: flex;
    align-items: center;

    &__item {
      flex: 0 0 auto;
      margin-right: 12px;
      line-height: 1;

      img {
        display: block;
        height: 28px;
        width: auto;
      }
    }
  }

  .event-nav {
    background-color: $color-white;
    box-shadow: $elevation-deth-4;
    position: sticky;
    top: 0;
    z-index: 2;

    &__container {
      max-width: 1044px;
      overflow: hidden;
      margin: 0 auto;
      padding-left: 16px;
      padding-right: 16px;

      @media (min-width: breakpoint(md)) {
        padding-left: 20px;
        padding-right: 20px;
      }

      @media (min-width: breakpoint(xl)) {
        padding-left: 24px;
        padding-right: 24px;
      }
    }
  }

  .reg-modal .base-modal__window {
    max-width: 728px;
  }

  .event-alerts .top-alerts {
    margin-bottom: 0;

    @media (min-width: 768px) {
      margin-bottom: -24px;
    }
  }
</style>
